import { apiClient } from '@/lib/api/client'
import type { ExportFormat } from '@/components/common/ExportButton'
import { enrichBlobAxiosError, saveAxiosBlobResponse } from '@/lib/utils/blobDownload'

export interface ReportFilters {
  client_id?: number | string
  from?: string
  to?: string
  team_id?: number | string
  group_by?: 'day' | 'week'
  staging?: boolean
  fe_user_id?: number | string
  view?: string
  channel?: string
}

export type TicketProductivityHandlingRow = {
  agent: string
  tickets_closed: number
  incident_requests: number
  service_requests: number
  avg_child_tickets: number
  pct_of_total_volume: number
}

export type TicketProductivityGrainRow = {
  period: string
  agent: string
  tickets_created: number
  tickets_closed: number
  incident_requests: number
  service_requests: number
}

export type TicketProductivityRow = TicketProductivityHandlingRow | TicketProductivityGrainRow

export interface TicketProductivityResponse {
  view: 'handling' | 'daily' | 'monthly' | string
  period?: { from: string; to: string }
  rows: TicketProductivityRow[]
  totals?: Record<string, number>
  filters: ReportFilters
}

export interface ReportsPeriodSummary {
  created: number
  closed: number
  net: number
  overdue_open: number
  unassigned_open: number
  sla_compliance_percent: number
  avg_first_response_seconds: number
}

export interface ReportsVolumePoint {
  date: string
  created: number
  closed: number
  net?: number
}

export interface ReportsStatusBreakdown {
  status: string
  status_code: string
  count: number
}

export interface ReportsSourceBreakdown {
  source: string
  source_code: string
  count: number
  color: string
}

export interface ReportsMetricRow {
  metric: string
  value: string | number
}

export interface KpiMetricBreakdownRow extends Record<string, string | number | null | undefined> {
  key: string
  metric: string
  formula: string
  met: number
  total: number
  threshold?: string | null
  result: string | number
}

export interface KpiDetailGroup {
  rows: Record<string, string | number | boolean>[]
  total: number
  shown: number
}

export interface KpiReportResponse {
  summary: Record<string, number>
  metric_breakdown: KpiMetricBreakdownRow[]
  details: Record<string, KpiDetailGroup>
  thresholds?: Record<string, number>
  filters: ReportFilters
}

export type SdKpiResponse = KpiReportResponse
export type FeKpiResponse = KpiReportResponse
export type AprDurationsResponse = KpiReportResponse

export interface ReportsOverviewResponse {
  period_summary: ReportsPeriodSummary
  volume_series: ReportsVolumePoint[]
  by_status: ReportsStatusBreakdown[]
  by_source: ReportsSourceBreakdown[]
  kpi_snapshot: ReportsMetricRow[]
  filters: ReportFilters
  stats?: Record<string, number>
  daily_volume?: Array<{ date: string; created: number }>
}

function toQuery(filters: ReportFilters): Record<string, string> {
  const q: Record<string, string> = {}
  if (filters.client_id) q.client_id = String(filters.client_id)
  if (filters.from) q.from = filters.from
  if (filters.to) q.to = filters.to
  if (filters.team_id) q.team_id = String(filters.team_id)
  if (filters.group_by) q.group_by = filters.group_by
  if (filters.staging) q.staging = '1'
  if (filters.fe_user_id) q.fe_user_id = String(filters.fe_user_id)
  if (filters.view) q.view = filters.view
  if (filters.channel) q.channel = filters.channel
  return q
}

export const helpdeskReportService = {
  async getOverview(filters: ReportFilters = {}): Promise<ReportsOverviewResponse> {
    const res = await apiClient.get('/helpdesk/reports/overview', { params: toQuery(filters) })
    return res.data
  },

  async getSla(filters: ReportFilters = {}) {
    const res = await apiClient.get('/helpdesk/reports/sla', { params: toQuery(filters) })
    return res.data
  },

  async getTasksReport(filters: ReportFilters = {}) {
    const res = await apiClient.get('/helpdesk/reports/tasks', { params: toQuery(filters) })
    return res.data as {
      summary: Record<string, number>
      tasks: Array<Record<string, string>>
      filters: ReportFilters
    }
  },

  async getAgents(filters: ReportFilters = {}, page = 1, perPage = 20) {
    const res = await apiClient.get('/helpdesk/reports/agents', {
      params: { ...toQuery(filters), page, per_page: perPage },
    })
    return res.data
  },

  async getVolume(filters: ReportFilters = {}) {
    const res = await apiClient.get('/helpdesk/reports/volume', { params: toQuery(filters) })
    return res.data
  },

  async getSdKpi(filters: ReportFilters = {}): Promise<SdKpiResponse> {
    const res = await apiClient.get('/helpdesk/reports/sd-kpi', { params: toQuery(filters) })
    return res.data
  },

  async getFeKpi(filters: ReportFilters = {}): Promise<FeKpiResponse> {
    const res = await apiClient.get('/helpdesk/reports/fe-kpi', { params: toQuery(filters) })
    return res.data
  },

  async getAprDurations(filters: ReportFilters = {}): Promise<AprDurationsResponse> {
    const res = await apiClient.get('/helpdesk/reports/apr-durations', { params: toQuery(filters) })
    return res.data
  },

  async getFeAttendance(filters: ReportFilters = {}, page = 1, perPage = 20) {
    const res = await apiClient.get('/helpdesk/reports/fe-attendance', {
      params: { ...toQuery(filters), page, per_page: perPage },
    })
    return res.data as {
      data: Array<Record<string, string | number | null>>
      meta: { total: number; per_page: number; current_page: number; last_page: number }
      filters: ReportFilters
    }
  },

  async getHistoricalPreview(filters: ReportFilters = {}, page = 1, perPage = 25) {
    const res = await apiClient.get('/helpdesk/reports/historical/preview', {
      params: { ...toQuery(filters), page, per_page: perPage },
    })
    return res.data as {
      count: number
      data: Array<Record<string, string>>
      columns: Record<string, string>
      meta: { total: number; per_page: number; current_page: number; last_page: number }
      filters: ReportFilters
    }
  },

  async getAgentProductivity(filters: ReportFilters = {}) {
    const res = await apiClient.get('/helpdesk/reports/agent-productivity', {
      params: toQuery(filters),
    })
    return res.data as {
      period: { from: string; to: string }
      counts: {
        raw_fd_rows: number
        raw_fp_rows: number
        tickets_closed: number
        agents_with_ticket_activity: number
        raw_status_rows: number
        attendance_rows: number
      }
      availability: {
        raw_fd: boolean
        raw_fp: boolean
        raw_statuses: boolean
        attendance: boolean
        notes: Record<string, string>
      }
      filters: ReportFilters
    }
  },

  async getAgentProductivityRaw(
    pack: 'fd' | 'fp',
    filters: ReportFilters = {},
    page = 1,
    perPage = 25
  ) {
    const res = await apiClient.get(`/helpdesk/reports/agent-productivity/raw/${pack}`, {
      params: { ...toQuery(filters), page, per_page: perPage },
    })
    return res.data as {
      pack: 'fd' | 'fp'
      headers: string[]
      data: Array<Array<string | number | null>>
      meta: { total: number; per_page: number; current_page: number; last_page: number }
      filters: ReportFilters
    }
  },

  async getTicketProductivity(
    filters: ReportFilters = {},
    view: 'handling' | 'daily' | 'monthly' | string = 'handling'
  ): Promise<TicketProductivityResponse> {
    const res = await apiClient.get('/helpdesk/reports/ticket-productivity', {
      params: { ...toQuery(filters), view },
    })
    return res.data as TicketProductivityResponse
  },

  async getContactCenterProductivity(filters: ReportFilters = {}) {
    const res = await apiClient.get('/helpdesk/reports/contact-center-productivity', {
      params: toQuery(filters),
    })
    return res.data as {
      monthly_pivot: Array<Record<string, string | number | null>>
      counts: { fact_rows: number; agents: number; batches: number }
      availability: { notes?: string }
      filters: ReportFilters
    }
  },

  async importContactCenterProductivity(file: File, filters: ReportFilters = {}, notes?: string) {
    const form = new FormData()
    form.append('file', file)
    if (filters.client_id != null && filters.client_id !== '') {
      form.append('client_id', String(filters.client_id))
    }
    if (notes) form.append('notes', notes)
    const res = await apiClient.post('/helpdesk/reports/contact-center-productivity/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return res.data as { inserted: number; skipped?: number; batch_id?: number }
  },

  async download(
    type: string,
    filters: ReportFilters,
    format: ExportFormat,
    options?: { pack?: 'full' | 'fd' | 'fp'; timeoutMs?: number }
  ): Promise<void> {
    const exportFormat = format === 'excel' ? 'xlsx' : format === 'pdf' ? 'pdf' : 'csv'
    const longTimeoutTypes = [
      'historical',
      'agent-productivity',
      'ticket-productivity',
      'contact-center-productivity',
    ]
    const timeout =
      options?.timeoutMs ??
      (longTimeoutTypes.includes(type) ? 300_000 : 60_000)

    try {
      const res = await apiClient.get(`/helpdesk/reports/${type}/download`, {
        params: {
          ...toQuery(filters),
          format: exportFormat,
          ...(options?.pack ? { pack: options.pack } : {}),
        },
        responseType: 'blob',
        timeout,
        headers: { Accept: '*/*' },
      })

      const packSuffix = options?.pack && options.pack !== 'full' ? `-${options.pack}` : ''
      saveAxiosBlobResponse(res, `helpdesk-${type}${packSuffix}.${exportFormat}`)
    } catch (error) {
      throw await enrichBlobAxiosError(error)
    }
  },
}
